/**
 * Notifications drawer (screenshots/53, brief §2.9): 420px over the current page. Today / Earlier groups,
 * unread dot + tinted row, a Team mentions filter, Mark all read. State and grouping: notifications.ts. Opens from the shell
 * ("notifications:open" event or #notifications); Esc or the scrim closes it and focus returns.
 */
import { useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { AtSign, CalendarDays, Check, FilePlus2, Gavel, ScrollText, Settings, Sparkles, TrendingDown, TrendingUp, X } from "lucide-react";
import { useShallow } from "zustand/react/shallow";
import { groupByDay, notificationsStore, unreadCount, useNotifications, type AppNotification } from "./notifications";
import { notificationTime } from "../account/logic";
import { navigate } from "../app/router";
import { duration, easing, prefersReducedMotion, reducedFallback } from "../motion";
import { useFocusTrap } from "../hooks/useFocusTrap";
import { List, ListRow, RowLead, RowMain, RowSub, RowTitle, RowTrail } from "../components/shared/List";

function TypeIcon({ n }: { n: AppNotification }) {
  const I = n.kind === "forecast" ? (n.direction === "up" ? TrendingUp : TrendingDown)
    : n.kind === "brief" ? Sparkles : n.kind === "mention" ? AtSign : n.kind === "agenda" ? CalendarDays
    : n.kind === "committee" ? ScrollText : n.kind === "filing" ? FilePlus2 : Gavel;
  return <span className="ntf-icon" data-type={n.kind} data-direction={n.direction} aria-hidden><I size={16} /></span>;
}

function Row({ n, now, onOpen }: { n: AppNotification; now: Date; onOpen: (n: AppNotification) => void }) {
  return (
    <ListRow className="ntf-row" data-unread={!n.read || undefined} onOpen={() => onOpen(n)}>
      <RowLead><TypeIcon n={n} /></RowLead>
      <RowMain min={120} className="ntf-text">
        <RowTitle className="ntf-title" lines={3}>{n.title}</RowTitle>
        <RowSub wrap className="ntf-detail">{n.detail}</RowSub>
      </RowMain>
      <RowTrail className="ntf-when">
        <time dateTime={n.at}>{notificationTime(n.at, now)}</time>
        {!n.read && <span className="ntf-dot" aria-label="Unread" />}
      </RowTrail>
    </ListRow>
  );
}

export function NotificationsDrawer() {
  const { open, list, filter, sample } = useNotifications(useShallow((s) => ({ open: s.open, list: s.items, filter: s.filter, sample: s.sample })));
  const unread = unreadCount(list);
  const panel = useRef<HTMLElement>(null);
  const opener = useRef<Element | null>(null);
  useFocusTrap(panel, open, { restore: false }); // Tab stays in the drawer; closing returns focus to the opener (below)

  // Open from anywhere: the event, or a #notifications link.
  useEffect(() => {
    const show = () => { opener.current = document.activeElement; notificationsStore.getState().setOpen(true); };
    const fromHash = () => { if (location.hash === "#notifications") { history.replaceState(null, "", location.pathname + location.search); show(); } };
    addEventListener("notifications:open", show);
    addEventListener("hashchange", fromHash);
    fromHash();
    return () => { removeEventListener("notifications:open", show); removeEventListener("hashchange", fromHash); };
  }, []);

  const close = () => { notificationsStore.getState().setOpen(false); (opener.current as HTMLElement | null)?.focus?.(); };
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") { e.preventDefault(); close(); } };
    addEventListener("keydown", onKey);
    return () => removeEventListener("keydown", onKey);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Slides in from the right (300 ms enter, motion "panels"); reduced motion: 100 ms fade.
  useLayoutEffect(() => {
    if (!open || !panel.current) return;
    panel.current.focus(); // the dialog itself takes focus; Tab reaches Mark all read, Close, the filter and the rows
    panel.current.animate(prefersReducedMotion() ? [{ opacity: 0 }, { opacity: 1 }] : [{ transform: "translateX(24px)", opacity: 0 }, { transform: "none", opacity: 1 }],
      prefersReducedMotion() ? { duration: reducedFallback.duration } : { duration: duration.slow, easing: easing.enter });
  }, [open]);

  const now = useMemo(() => new Date(), [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const { today, earlier } = useMemo(() => groupByDay(filter === "mentions" ? list.filter((n) => n.kind === "mention") : list, now), [list, now, filter]);
  const teamCount = list.filter((n) => n.kind === "mention").length;
  if (!open) return null;

  const openOne = (n: AppNotification) => {
    notificationsStore.getState().markRead(n.id);
    if (n.href) { notificationsStore.getState().setOpen(false); navigate(n.href); }
  };

  return (
    <div className="ntf-layer" onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}>
      <aside className="ntf-drawer" role="dialog" aria-modal="true" aria-label="Notifications" ref={panel} tabIndex={-1}>
        <header className="ntf-head">
          <h2>Notifications</h2>
          {unread > 0 && <span className="ntf-new">{unread} new</span>}
          <span className="spacer" />
          <button className="ntf-markall" onClick={() => notificationsStore.getState().markAllRead()}>Mark all read<Check size={16} /></button>
          <button className="ntf-close" aria-label="Close" onClick={close}><X size={18} /></button>
        </header>
        <div className="ntf-filter">
          <div className="view-switch" role="radiogroup" aria-label="Show">
            <button role="radio" aria-checked={filter === "all"} onClick={() => notificationsStore.getState().setFilter("all")}>All · {list.length}</button>
            <button role="radio" aria-checked={filter === "mentions"} onClick={() => notificationsStore.getState().setFilter("mentions")}>Team mentions · {teamCount}</button>
          </div>
        </div>
        <div className="ntf-body">
          {today.length > 0 && (
            <section aria-label="Today">
              <h3 className="ntf-group">Today</h3>
              <List variant="plain" className="ntf-list">{today.map((n) => <Row key={n.id} n={n} now={now} onOpen={openOne} />)}</List>
            </section>
          )}
          {earlier.length > 0 && (
            <section aria-label="Earlier" data-after={today.length > 0 || undefined}>
              <h3 className="ntf-group">Earlier</h3>
              <List variant="plain" className="ntf-list">{earlier.map((n) => <Row key={n.id} n={n} now={now} onOpen={openOne} />)}</List>
            </section>
          )}
          {today.length + earlier.length === 0 && <p className="ntf-empty">You're all caught up.</p>}
        </div>
        <footer className="ntf-foot">
          <a className="ntf-settings" href="/dashboard/settings#notifications" onClick={(e) => { e.preventDefault(); notificationsStore.getState().setOpen(false); navigate("/dashboard/settings#notifications"); }}>
            Notification settings<Settings size={16} />
          </a>
        </footer>
      </aside>
    </div>
  );
}
